// @flow
import React, {Fragment, useEffect, useState, useMemo} from 'react';
import PropTypes from 'prop-types';
import {Redirect, useParams, Link} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {makeStyles} from '@material-ui/core/styles';
import {
  IconButton,
  Typography,
  Grid,
  Hidden,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import {ArrowBack, Delete} from '@material-ui/icons';
import SnackBar from '../../../../elements/SnackBar/SnackBar';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import WhatsAppOption from './WhatsAppOption';
import CustomExitPrompt from '../../../../elements/CustomExitPrompt/CustomExitPrompt';
import MercadoPagoConfigContainer from '../containers/MercadoPagoConfigContainer';
import IziPayConfigContainer from '../containers/IziPayConfigContainer';
import LogoPaymentMethodForm from './LogoPaymentMethodForm';

import {colors} from '../../../../assets/styles/colors';
import {useIsFeatureInTier} from '../../../../utils/component';

import type {
  Store,
  PaymentMethod,
  ShippingMethodLogistic,
} from '../../../../entities';
import type {PaymentMethodAction} from '../duck';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import PayOnDelivery from './paymentProviderConfigs/PayOnDeliveryConfig';
import CulqiConfigContainer from '../containers/CulqiConfigContainer';
import StripeConfigContainer from '../containers/StripeConfigContainer';
import PaypalConfigContainer from '../containers/PaypalConfigContainer';
import TransbankConfigContainer from '../containers/TransbankConfigContainer';
import NiubizWebConfigContainer from '../containers/NiubizWebConfigContainer';
import NiubizPayLinkConfigContainer from '../containers/NiubizPayLinkConfigContainer';

type Props = {
  isFetching?: boolean,
  isSaving?: boolean,
  isDeleting?: boolean,
  addSuccess?: boolean,
  updateSuccess?: boolean,
  deleteSuccess?: boolean,
  error?: string,
  addPaymentMethod: ({paymentMethodRequest: PaymentMethod}) => void,
  addLogoPaymentMethod: (params: PaymentMethodAction) => void,
  deletePaymentMethod: ({id: string}) => void,
  updatePaymentMethod: ({
    paymentMethodRequest: {
      enabled: boolean,
      displayName: string,
      fileId?: string,
      clientParameters?: {
        description: string,
        identifier: string,
      },
    },
    typePaymentMethod: string,
    paymentMethodId: string,
  }) => void,
  paymentMethod: PaymentMethod,
  paymentMethods: PaymentMethod[],
  getPaymentMethod: ({id: string}) => void,
  resetState: () => void,
  store: Store,
  shippingMethod?: ShippingMethodLogistic,
};

const useStyles = makeStyles(theme => ({
  checkbox: {
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(2),
  },
  checkboxLabel: {
    color: colors.grey700,
    fontSize: '0.875rem',
    // margin: theme.spacing(3, 0),
  },
  snackBarSuccess: {
    backgroundColor: '#E5F7F2',
    color: '#00B282',
  },
  snackBarError: {
    backgroundColor: '#FDECEA',
    color: '#A05536',
  },
  subRouteHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: theme.spacing(1, 0),
  },
  paymentMethodHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
  subRouteHeaderSmall: {
    display: 'flex',
    padding: '8px 16px',
    alignItems: 'center',
    margin: 'auto',
    justifyContent: 'center',
    backgroundColor: '#F8F9FB',
  },
  subRouterBody: {
    margin: theme.spacing(1, 0),
  },
  subRouteTitle: {
    marginLeft: theme.spacing(2),
    fontSize: '1rem',
    fontWeight: 600,
  },
  deleteButton: {
    backgroundColor: 'transarent',
    color: '#E54D7B',
    borderRadius: '32px',
    [theme.breakpoints.up('sm')]: {
      width: 200,
    },
  },
  saveButton: {
    margin: theme.spacing(2, 0),
    borderRadius: '32px',
    [theme.breakpoints.up('sm')]: {
      width: 200,
    },
    [theme.breakpoints.down('xs')]: {
      zIndex: 1,
      position: 'fixed',
      left: 0,
      bottom: 64,
      margin: theme.spacing(2),
      height: 48,
      width: `calc(100% - ${theme.spacing(4)})`,
      fontSize: '1.125rem',
    },
  },
  gridFormulario: {
    padding: theme.spacing(1, 0),
  },
  saveAlert: {
    // width: '100%',
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(2),
    },
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),

    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: '1fr 1fr',
    },
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: '1fr 1fr 1fr',
    },
  },
  bankOption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '.85rem',

    '&[data-selected-option]': {
      padding: 0,
    },
  },
  bankPicture: {
    width: 100,
    height: 20,
    '& img': {
      objectFit: 'contain',
      width: '100%',
      height: '100%',
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const CONTINUE_TO_CHAT = 'CONTINUE_TO_CHAT';
const PAY_ON_DELIVERY = 'PAY_ON_DELIVERY';

export default function NewPaymentMethod(props: Props) {
  const {
    isFetching,
    isSaving,
    isDeleting,
    addSuccess,
    updateSuccess,
    error,
    updatePaymentMethod,
    deletePaymentMethod,
    paymentMethod,
    paymentMethods,
    getPaymentMethod,
    deleteSuccess,
    store,
    shippingMethod,
    addLogoPaymentMethod,
  } = props;
  const classes = useStyles();

  const hasPaymentProcessorFeature = useIsFeatureInTier('PAYMENT_PROCESSOR');

  const hasPmMethodDisabled = !hasPaymentProcessorFeature;

  const {handleSubmit, register, formState, errors} = useForm({
    mode: 'onChange',
  });
  const {isDirty} = formState;
  const [shippingError, setShippingError] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [enabled, setEnabled] = useState(
    paymentMethod ? paymentMethod.enabled : false
  );

  const [
    minCantPaymentMethodsEnabled,
    setMinCantPaymentMethodsEnabled,
  ] = useState(false);

  const handleCloseDeleteModal = () => {
    setOpenModalDelete(false);
  };

  const handleOpenDeleteModal = () => {
    setOpenModalDelete(true);
  };

  const handleDeleteItem = () => {
    if (paymentMethod)
      deletePaymentMethod({
        id: paymentMethod.id,
      });
    handleCloseDeleteModal();
  };

  console.log(shippingMethod);
  const canDisableButton = useMemo(() => {
    return (
      shippingMethod &&
      shippingMethod.storeShippingMethodType === 'ADVANCED' &&
      shippingMethod.storeShippingMethodAdvancedSetting &&
      shippingMethod.storeShippingMethodAdvancedSetting
        .enabledAdminOutsideShipping
    );
  }, [shippingMethod]);

  const handleToggleEnabled = event => {
    if (event) {
      if (
        paymentMethod.paymentMethod === CONTINUE_TO_CHAT &&
        !event.target.checked &&
        canDisableButton
      ) {
        setShippingError(true);
        return;
      }
      setEnabled(event.target.checked);
      if (hasPmMethodDisabled) return;
      if (
        !event.target.checked ||
        paymentMethod.paymentMethod === CONTINUE_TO_CHAT ||
        paymentMethod.paymentMethod === PAY_ON_DELIVERY
      ) {
        const paymentMethodRequest = {
          ...paymentMethod,
          enabled: event.target.checked,
        };

        let bodyRequest = {
          enabled: paymentMethodRequest.enabled,
          displayName: paymentMethodRequest.displayName,
        };
        if (paymentMethodRequest.clientParameters)
          bodyRequest = {
            ...bodyRequest,
            clientParameters: paymentMethodRequest.clientParameters,
          };

        if (paymentMethodRequest.fileId)
          bodyRequest = {
            ...bodyRequest,
            fileId: paymentMethodRequest.fileId,
          };

        let minCant = 0;
        for (let i = 0; i < paymentMethods.length; i++) {
          if (paymentMethods[i].enabled) minCant += 1;
        }

        if (minCant > 1 || event.target.checked) {
          setMinCantPaymentMethodsEnabled(false);
          updatePaymentMethod({
            paymentMethodRequest: bodyRequest,
            typePaymentMethod: paymentMethodRequest.paymentMethod,
            paymentMethodId: paymentMethodRequest.id,
          });
        } else {
          setEnabled(true);
          setMinCantPaymentMethodsEnabled(true);
        }
      }
    }
  };

  const params = useParams();

  console.log({
    paymentMethod,
    params,
  });

  const paymentMethodId = params?.id;

  useEffect(() => {
    if (!paymentMethodId) return;
    getPaymentMethod({id: paymentMethodId});
  }, [getPaymentMethod, paymentMethodId]);

  useEffect(() => {
    if (paymentMethod) {
      setEnabled(paymentMethod.enabled);
    } else {
      setEnabled(true);
    }
  }, [paymentMethod]);

  const disableActivation = React.useMemo(() => {
    return false;
  }, []);

  if ((deleteSuccess && !openModalDelete) || addSuccess || updateSuccess) {
    return <Redirect to={'/configure/payment-methods'} />;
  }

  if (isFetching) return null;

  if (paymentMethod?.paymentMethod === CONTINUE_TO_CHAT) {
    return (
      !isFetching && (
        <WhatsAppOption
          store={store}
          handleToggleEnabled={handleToggleEnabled}
          enabled={enabled}
          isSaving={isSaving}
          error={error}
          shippingError={shippingError}
          updateSuccess={updateSuccess}
        />
      )
    );
  }

  if (paymentMethod?.paymentMethod === PAY_ON_DELIVERY) {
    return (
      <PayOnDelivery
        store={store}
        handleToggleEnabled={handleToggleEnabled}
        enabled={enabled}
        isSaving={isSaving}
        error={error}
        shippingError={shippingError}
        updateSuccess={updateSuccess}
      />
    );
  }

  if (paymentMethod?.paymentProcessor.code === 'MERCADOPAGO_CHECKOUT_PRO') {
    return (
      !isFetching && (
        <MercadoPagoConfigContainer
          handleSubmit={handleSubmit}
          register={register}
          formState={formState}
          errors={errors}
          enabled={enabled}
          handleToggleEnabled={handleToggleEnabled}
          disableActivation={disableActivation}
        />
      )
    );
  }

  if (paymentMethod?.paymentProcessor.code === 'IZIPAY') {
    return (
      !isFetching && (
        <IziPayConfigContainer disableActivation={disableActivation} />
      )
    );
  }

  if (paymentMethod?.paymentProcessor.code === 'CULQI') {
    return (
      !isFetching && (
        <CulqiConfigContainer disableActivation={disableActivation} />
      )
    );
  }

  if (paymentMethod?.paymentProcessor.code === 'STRIPE_CHECKOUT') {
    return (
      !isFetching && (
        <StripeConfigContainer disableActivation={disableActivation} />
      )
    );
  }

  if (paymentMethod?.paymentProcessor.code === 'PAYPAL') {
    return (
      !isFetching && (
        <PaypalConfigContainer disableActivation={disableActivation} />
      )
    );
  }

  if (paymentMethod?.paymentProcessor.code === 'TRANSBANK') {
    return (
      !isFetching && (
        <TransbankConfigContainer disableActivation={disableActivation} />
      )
    );
  }

  if (paymentMethod?.paymentProcessor.code === 'NIUBIZ_WEB') {
    return (
      !isFetching && (
        <NiubizWebConfigContainer disableActivation={disableActivation} />
      )
    );
  }

  if (paymentMethod?.paymentProcessor.code === 'NIUBIZ_PAY_LINK') {
    return (
      !isFetching && (
        <NiubizPayLinkConfigContainer disableActivation={disableActivation} />
      )
    );
  }

  return (
    <Fragment>
      <Grid container alignContent="flex-start">
        <Grid item xs={12} className={classes.gridFormulario}>
          <CustomExitPrompt when={isDirty} />
          <div className={classes.subRouteHeader}>
            <header className={classes.paymentMethodHeader}>
              <Hidden xsDown>
                <IconButton
                  variant="contained"
                  size="small"
                  component={Link}
                  to={`/configure/payment-methods${window.location.search}`}
                >
                  <ArrowBack />
                </IconButton>
              </Hidden>
              <Typography className={classes.subRouteTitle}>
                {paymentMethod && paymentMethod.displayName
                  ? paymentMethod.displayName
                  : 'Añadir Nuevo'}
              </Typography>
            </header>
            <CustomButton
              onClick={handleOpenDeleteModal}
              className={classes.deleteButton}
            >
              <Delete />
              Eliminar
            </CustomButton>
          </div>
          <div className={classes.subRouterBody}>
            <Typography style={{margin: '15px'}}>Completa los datos</Typography>
            {paymentMethod && typeof disableActivation === 'boolean' && (
              <FormControlLabel
                classes={{label: classes.checkboxLabel}}
                className={classes.checkbox}
                control={
                  <Checkbox
                    checked={enabled}
                    onChange={handleToggleEnabled}
                    color="primary"
                    disabled={isSaving || disableActivation}
                  />
                }
                label={
                  disableActivation
                    ? 'Debes configurar tus propias tarifas de envío para habilitar esta opción'
                    : 'Activar el método de pago'
                }
              />
            )}
            {enabled && (
              <>
                <LogoPaymentMethodForm
                  hasPmMethodDisabled={hasPmMethodDisabled}
                  isSaving={!!isSaving}
                  paymentMethod={paymentMethod}
                  addLogoPaymentMethod={addLogoPaymentMethod}
                />
              </>
            )}
          </div>

          {/* <Hidden smUp>
          <div className={classes.subRouteHeaderSmall}>
            <Button
              size="large"
              color="primary"
              fullWidth
              component={Link}
              to="/configure/payment-methods"
              disabled={isSaving}
              startIcon={<ArrowBack />}
            >
              <Typography color="primary" className={classes.subRouteTitle}>
                Regresar
              </Typography>
            </Button>
          </div>
        </Hidden> */}
        </Grid>
      </Grid>

      {minCantPaymentMethodsEnabled && (
        <SnackBar
          mode="error"
          className={classes.snackBarError}
          message={'Debe tener al menos un método de pago seleccionado'}
        />
      )}

      <SnackBar
        mode="error"
        className={classes.snackBarError}
        message={error}
      />
      <SnackBar
        mode="success"
        className={classes.snackBarSuccess}
        message={
          updateSuccess ? 'Se guardaron los cambios exitosamente' : undefined
        }
      />

      <DeleteConfirmationModal
        open={openModalDelete}
        handleClose={handleCloseDeleteModal}
        handleDeleteClick={handleDeleteItem}
        isDeleting={isDeleting ? isDeleting : false}
      />
    </Fragment>
  );
}

NewPaymentMethod.propTypes = {
  isDeleting: PropTypes.bool,
  isFetching: PropTypes.bool,
  isSaving: PropTypes.bool,
  error: PropTypes.string,
  paymentMethod: PropTypes.object,
  paymentMethods: PropTypes.array,
  addSuccess: PropTypes.bool,
  deleteSuccess: PropTypes.bool,
  updateSuccess: PropTypes.bool,
  addPaymentMethod: PropTypes.func.isRequired,
  deletePaymentMethod: PropTypes.func.isRequired,
  updatePaymentMethod: PropTypes.func.isRequired,
  resetState: PropTypes.func.isRequired,
  getPaymentMethod: PropTypes.func.isRequired,
  store: PropTypes.object,
  shippingMethod: PropTypes.object,
};
